/**
 * The document half of the double-tap-zoom suppression whose other half is the
 * `@layer base { :host, * }` rule in `tailwind.css`.
 *
 * Pointer Events Level 3 §8.2: "A direct manipulation interaction for zooming
 * is supported if it conforms to the touch-action property of EACH element
 * between the hit tested element and the document element of the top-level
 * browsing context." Every zoom chain therefore terminates at `html`, and the
 * baked mixin sheet cannot reach it — that sheet is adopted into shadow roots,
 * and the light-DOM elements above a component, `html` included, never see it.
 * Left at `auto`, the document element conforms to double-tap zoom; the chain
 * as a whole still does not, because the hit target inside the component says
 * `manipulation` — which is why the shadow half works on its own and why this
 * one is the belt to its braces rather than a second implementation. Landing it
 * here rather than in each app's own `index.css` is the point: an app-level
 * `html { touch-action: manipulation }` is a line every consumer and every fork
 * copies, and one fork forgetting it is silent.
 *
 * `manipulation` and no other value — settled in the mixin sheet's own comment
 * and not re-decided here: double-tap zoom off, pan and pinch (WCAG 1.4.4)
 * left alone.
 *
 * Three properties this shape buys:
 *
 * - It cannot fight a consumer. The rule is layered into Tailwind's own `base`,
 *   which `utilities` outranks and which every unlayered declaration outranks,
 *   so `html { touch-action: auto }` at a consumer's root — the one place the
 *   value can be overridden, since the chain intersects and no descendant can
 *   hand a behaviour back — wins without `!important`.
 * - It reaches only `:root`. A universal selector at document level would beat
 *   a NON-schmancy custom element's own `:host { touch-action: none }`, because
 *   a normal declaration from the outer tree outranks an inner-tree one at any
 *   layer — the same cascade fact `<schmancy-dock>`'s `!important` grip exists
 *   for. The terminus is the whole job here; the elements below it are the
 *   shadow half's.
 * - It is per-document. A constructed sheet belongs to the window that built
 *   it, so the document's own view is the only one whose sheet it accepts.
 */
const ROOT_FLOOR = `@layer base { :root { touch-action: manipulation } }`

const floored = new WeakSet<Document>()

/**
 * Idempotent per document, called from `BaseElement.connectedCallback` — so the
 * floor lands exactly when a schmancy component enters a document, which is
 * exactly when the shadow half starts making its promise. An app that imports
 * the library and mounts nothing gets nothing.
 */
export function suppressDoubleTapZoom(doc: Document | null | undefined): void {
	if (!doc || floored.has(doc)) return
	const view = doc.defaultView
	if (!view || typeof view.CSSStyleSheet !== 'function' || !('adoptedStyleSheets' in doc)) return

	floored.add(doc)
	try {
		const sheet = new view.CSSStyleSheet()
		sheet.replaceSync(ROOT_FLOOR)
		doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet]
	} catch {
		// A DOM emulation without constructible stylesheets throws here, and a
		// consumer's node-lane suite mounts components in exactly that
		// environment — where there is no gesture to suppress. Marked floored
		// before the attempt so the throw is paid once per document, never once
		// per connect.
	}
}
